// Wrapper Component for the Drawer Component
import { ReactElement, ReactNode } from 'react';
import { Drawer as MantineDrawer } from '@mantine/core';
import styles from './Drawer.module.scss';

// Link to Mantine Drawer Component:  https://mantine.dev/core/drawer/

interface Props {
  isOpen: boolean;
  onClose: () => void;
  content?: ReactNode;
  position?: 'right' | 'left';
  header?: ReactNode;
  size?: string;
  offsetTop?: string;
  zIndex?: number;
  trapFocus?: boolean;
  isHeaderCentered?: boolean;
  overrideStyles?: any;
}

const drawerStyles = (offsetTop: string, isHeaderCentered: boolean, additionalStyles: any) => {
  const overrideStyles: any = {
    drawer: {
      top: offsetTop,
      height: `calc(100vh - ${offsetTop})`,
      [`@supports (height: 100dvh)`]: {
        height: `calc(100dvh - ${offsetTop})`,
      },
      display: 'flex',
      flexDirection: 'column',
    },
    overlay: {
      top: offsetTop,
      height: `calc(100vh - ${offsetTop})`,
      [`@supports (height: 100dvh)`]: {
        height: `calc(100dvh - ${offsetTop})`,
      },
      backgroundColor: 'rgba(0, 0, 0, 0.3) !important',
    },
    closeButton: {
      color: 'black',
      border: 'none',
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'black',
        border: 'none',
        boxShadow: 'none',
      },
    },
  };

  if (isHeaderCentered) {
    // override Mantine styling to center the title without moving the close button
    overrideStyles.closeButton.position = 'absolute';
    overrideStyles.closeButton.right = 20;
    overrideStyles.header = { position: 'relative' };
    overrideStyles.title = {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      margin: 0,
    };
  }

  if (additionalStyles) {
    return { ...overrideStyles, ...additionalStyles };
  }

  return overrideStyles;
};

export function Drawer({
  isOpen,
  onClose,
  content,
  position = 'right',
  header,
  size = 'xl',
  offsetTop = '0px',
  zIndex,
  trapFocus = true,
  isHeaderCentered = false,
  overrideStyles,
}: Props): ReactElement {
  return (
    <MantineDrawer
      opened={isOpen}
      onClose={onClose}
      position={position}
      title={header}
      size={size}
      withCloseButton={!!header}
      zIndex={zIndex}
      trapFocus={trapFocus}
      classNames={{
        header: styles.header,
        body: styles.body,
      }}
      styles={drawerStyles(offsetTop, isHeaderCentered, overrideStyles)}
    >
      {content}
    </MantineDrawer>
  );
}
